import * as React from "react";
import { ReactRenderer } from "@tiptap/react";
import type { SuggestionOptions } from "@tiptap/suggestion";
import type { Instance as TippyInstance } from "tippy.js";
import { createCollaborateSuggestionTippy } from "./collaborateSuggestionTippy";
import CollaborateSlashList, {
  type CollaborateSlashListRef,
} from "../components/editor/CollaborateSlashList";
import { filterSlashCommands, type SlashCommandItem } from "./collaborateSlashCommands";
import { insertPlaybookBadge } from "./collaboratePlaybookChips";
import { insertSignoffBadge } from "./collaborateSignoffChips";
import { showCollabToast } from "../components/ui/CollabToast";

export function createCollaborateSlashSuggestion(
  hasDocumentSelection: () => boolean,
): Omit<SuggestionOptions, "editor"> {
  return {
    char: "/",
    allowSpaces: false,
    items: ({ query }) => filterSlashCommands(query),
    command: ({ editor, range, props }) => {
      editor.chain().focus().deleteRange(range).run();
      const item = props as SlashCommandItem | undefined;
      if (item?.id === "update-playbook") {
        if (!hasDocumentSelection()) {
          showCollabToast.warning("Select text in the document first to update the playbook.");
          return;
        }
        insertPlaybookBadge(editor);
        return;
      }
      insertSignoffBadge(editor);
    },
    render: () => {
      let component: ReactRenderer<CollaborateSlashListRef> | null = null;
      let popup: TippyInstance[] | null = null;

      return {
        onStart: (props) => {
          component = new ReactRenderer(CollaborateSlashList, {
            props: {
              items: props.items as SlashCommandItem[],
              command: (item: SlashCommandItem) => {
                props.command(item);
              },
            },
            editor: props.editor,
          });

          if (!props.clientRect) {
            return;
          }

          popup = createCollaborateSuggestionTippy({
            getReferenceClientRect: props.clientRect as () => DOMRect,
            content: component.element,
          });
        },

        onUpdate(props) {
          component?.updateProps({
            items: props.items as SlashCommandItem[],
            command: (item: SlashCommandItem) => {
              props.command(item);
            },
          });
          if (popup?.[0] && props.clientRect) {
            popup[0].setProps({
              getReferenceClientRect: props.clientRect as () => DOMRect,
            });
          }
        },

        onKeyDown(props) {
          if (props.event.key === "Escape") {
            popup?.[0]?.hide();
            return true;
          }
          return component?.ref?.onKeyDown(props) ?? false;
        },

        onExit() {
          popup?.[0]?.destroy();
          component?.destroy();
          popup = null;
          component = null;
        },
      };
    },
  };
}
